import React, { Component } from 'react';
import {
  AuthenticationService,
  PageContainer,
} from '@knockrentals/knock-react';
import { ThemeProvider } from '@knockrentals/knock-shared-web';
import LinkRegistration from './LinkRegistration';
import RegistrationDisplay from './RegistrationDisplay';
import RegistrationAPI from './RegistrationAPI';
import { normalizeRegistration, showToastError } from './utils';
import { ERROR_MESSAGES, TOAST_ERROR_MESSAGES } from './constants';
import './_registration.scss';

class RegistrationPage extends Component {
  state = {
    errorMessage: '',
    existingKnockRegistration: null,
    existingBrandRegistration: null,
    existingCustomerProfile: null,
    isLoading: true,
    phoneRegistration: null,
    showInstruction: { z: true },
    thisCompanyName: '',
  };

  getRegistration = async () => {
    try {
      const response = await RegistrationAPI.getRegistration();

      if (response.status === 418) {
        throw new Error(ERROR_MESSAGES.USER);
      }
      if (response.status !== 200) {
        throw new Error();
      }

      const responseBody = await response.json();

      this.setState({
        thisCompanyName: responseBody.company_name,
        existingKnockRegistration: responseBody.knock_registration,
        existingBrandRegistration: responseBody.twilio_brand_registration,
        existingCustomerProfile: responseBody.twilio_customer_profile,
        existingMessagingService: responseBody.twilio_messaging_service || {},
        existingA2PCampaign: responseBody.twilio_a2p_campaign || {},
      });
    } catch (e) {
      const errorMessage =
        e.message === ERROR_MESSAGES.USER
          ? ERROR_MESSAGES.USER
          : ERROR_MESSAGES.UNKNOWN;

      this.setState({ errorMessage });

      showToastError(TOAST_ERROR_MESSAGES.EXISTING_REGISTRATION_FETCH);
    }
  };

  getCompanyPhoneRegistration = async () => {
    try {
      const response = await RegistrationAPI.getCompanyPhoneRegistration();
      const {
        capacity,
        total_registered: totalRegistered,
        total_unregistered: totalUnregistered,
        twilio_limit: twilioLimit,
      } = response;

      this.setState({
        phoneRegistration: {
          capacity,
          totalRegistered,
          totalUnregistered,
          twilioLimit,
        },
      });
    } catch {
      this.setState({ errorMessage: ERROR_MESSAGES.UNKNOWN });
      showToastError(TOAST_ERROR_MESSAGES.EXISTING_REGISTRATION_FETCH);
    }
  };

  initRegistration = async () => {
    await this.getRegistration();

    const shouldCallCompanyPhoneRegistration =
      AuthenticationService._internalMode &&
      this.state.existingBrandRegistration &&
      !this.state.errorMessage;

    if (shouldCallCompanyPhoneRegistration) {
      await this.getCompanyPhoneRegistration();
    }

    this.setState({ isLoading: false });
  };

  componentDidMount() {
    this.initRegistration();
  }

  render() {
    return (
      <section>
        <PageContainer isLoading={this.state.isLoading}>
          <h1>Registration</h1>
          <div className="knock-react-flex knock-react-flex-row">
            <div className="a2p-registration-container">
              <h2>
                <i className="fa fa-address-book" /> Twilio Brand Registration:
                A2P-10DLC & SHAKEN/STIR
              </h2>

              {!this.state.isLoading &&
                (this.state.errorMessage ? (
                  <span>{this.state.errorMessage}</span>
                ) : (
                  this.renderAuthorizedContainer()
                ))}
            </div>
          </div>
        </PageContainer>
      </section>
    );
  }

  renderExistingRegistration = () => {
    const registration = {
      ...normalizeRegistration({
        knock_registration: this.state.existingKnockRegistration,
        twilio_brand_registration: this.state.existingBrandRegistration,
        twilio_customer_profile: this.state.existingCustomerProfile,
        twilio_messaging_service: this.state.existingMessagingService,
        twilio_a2p_campaign: this.state.existingA2PCampaign,
      }),
      phoneRegistration: this.state.phoneRegistration,
    };

    return (
      <ThemeProvider>
        <RegistrationDisplay registration={registration} />
      </ThemeProvider>
    );
  };

  renderAuthorizedContainer() {
    return (
      <div>
        <div>
          <span>Currently logged in for Knock Company: </span>
          <span className="larger bold">{this.state.thisCompanyName}</span>
          <br />
        </div>

        {this.state.existingBrandRegistration &&
        this.state.existingCustomerProfile
          ? this.renderExistingRegistration()
          : this.renderA2pRegistrationContainer()}
      </div>
    );
  }

  renderA2pRegistrationContainer() {
    return (
      <div>
        <ThemeProvider>
          <LinkRegistration />
        </ThemeProvider>
        {this.renderRegistrationInstructions()}
      </div>
    );
  }

  renderRegistrationInstructions() {
    return (
      <div className="brand-registration-instructions">
        <h3 className="side-margin-lg">Registration FAQs</h3>
        <ul>
          <li
            className="question"
            onClick={this.toggleInstruction.bind(this, 'a')}
          >
            What is Twilio Brand Registration and why is it needed?
          </li>
          <ul
            className={`answer ${!this.state.showInstruction.a ? 'hide' : ''}`}
          >
            <li>
              This is an industry wide effort, led by major carriers like AT&T
              and T-Mobile to cut down on spam traffic on the networks.
              Registering puts us in a compliant state so that Twilio and
              carriers can better identify traffic belonging to legitimate
              businesses like ourselves and our customers, giving each of us a
              Trust Score and helping our communications to avoid getting
              filtered.
            </li>
            <li>
              For more information, read up{' '}
              <a
                href="https://support.twilio.com/hc/en-us/articles/1260800720410-What-is-A2P-10DLC-"
                target="_blank"
                rel="noreferrer"
              >
                here on Twilio
              </a>
              .
            </li>
          </ul>

          <li
            className="question"
            onClick={this.toggleInstruction.bind(this, 'b')}
          >
            What is a Twilio Brand Registration SID?
          </li>
          <ul
            className={`answer ${!this.state.showInstruction.b ? 'hide' : ''}`}
          >
            <li>
              This is a unique identifier for a Brand that you will find on
              Twilio after completing a registration on the Twilio console on
              behalf of the business (i.e. knock customer).
            </li>
            <li>
              For instructions on how to complete a registration on Twilio to
              get a Brand Registration SID, please{' '}
              <a
                href="https://knockr.atlassian.net/wiki/spaces/CO/pages/1707704321/Twilio+Brand+Registration+-+How+To"
                target="_blank"
                rel="noreferrer"
              >
                follow this guide
              </a>
              .
            </li>
          </ul>

          <li
            className="question"
            onClick={this.toggleInstruction.bind(this, 'c')}
          >
            How should I register a business that has multiple Knock
            accounts/companies?
          </li>
          <ul
            className={`answer ${!this.state.showInstruction.c ? 'hide' : ''}`}
          >
            <li>
              A common scenario at Knock: a customer (e.g. ABC Mgmt) is setup
              with multiple Knock companies (e.g. "ABC Hold", "ABC Pilot", "ABC
              Co."). In this case, "ABC Mgmt" is registered once on Twilio to
              get a Brand Registration SID. This Brand Registration SID should
              then be used to register all 3 Knock companies (Hold, Pilot, Co.)
              belonging to that customer.
            </li>
            <li>
              In essence, a Knock company can only be linked to one Twilio Brand
              Registration while a single Twilio Brand Registration can be
              linked to multiple Knock companies.
            </li>
          </ul>

          <li
            className="question"
            onClick={this.toggleInstruction.bind(this, 'd')}
          >
            How should I register Knock companies that are only for internal
            use?
          </li>
          <ul
            className={`answer ${!this.state.showInstruction.d ? 'hide' : ''}`}
          >
            <li>
              There is a Brand Registration on Twilio that is made under Knock's
              name. However, please be careful to use this only for Knock
              companies that are expected to have minimal phone call and
              messaging traffic. Any traffic that looks fraudulent/malicious to
              carriers will directly impact Knock's reputation with them and
              affect our Trust Score.
            </li>
          </ul>

          <li
            className="question"
            onClick={this.toggleInstruction.bind(this, 'e')}
          >
            What happens after a Knock Company is linked to a Twilio Brand
            Registration using this tool?
          </li>
          <ul
            className={`answer ${!this.state.showInstruction.e ? 'hide' : ''}`}
          >
            <li>
              Immediately after linking this Knock company to the Twilio Brand
              Registration, all community relay tracking numbers on the Knock
              company will be programmatically assigned to the Twilio Brand
              Registration.
            </li>
            <li>
              Under the hood, we programmatically setup the other required
              Messaging Service, Campaign Use Case, and Message Samples on
              Twilio for you, so you do not have to do much more than the
              initial creation of the Twilio Brand Registration which requires
              the input of the customer's business information.
            </li>
            <li>
              After the link is created and moving forward, all newly added
              community relay tracking numbers for this registered Knock company
              will automatically be assigned to the Twilio Brand Registration,
              which reduces the amount of maintenance needed by this compliance
              requirement.
            </li>
          </ul>

          <li
            className="question"
            onClick={this.toggleInstruction.bind(this, 'z')}
          >
            Anything else that I should know?
          </li>
          <ul
            className={`answer ${!this.state.showInstruction.z ? 'hide' : ''}`}
          >
            <li>
              A Knock company cannot be unlinked from a Twilio Brand
              Registration once it is set.
            </li>
            <li>
              Be sure to verify that the Twilio Brand Registration details from
              the Lookup are correct for the Knock company you are trying to
              register before you "Confirm Registration"
            </li>
          </ul>
        </ul>
      </div>
    );
  }

  toggleInstruction(section) {
    const showInstruction = this.state.showInstruction;
    showInstruction[section] = !showInstruction[section];
    this.setState({ showInstruction: showInstruction });
  }
}

export default RegistrationPage;
